import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logoPath from "@/assets/barcio-logo.png";

const CAUSALE_LABEL = {
  consegna: "Consegna",
  ritiro: "Ritiro",
  reso: "Reso",
};

/**
 * Genera un PDF DDT e lo restituisce come Blob.
 * @param {Object} ddt - { id, causale, date, client?, magazziniere?, operatore?, ddt_items }
 * @returns {Blob}
 */
export function generateDdtPdf(ddt) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 10;
  let y = 10;

  // ---- HEADER ----
  if (logoPath) {
    try {
      doc.addImage(logoPath, "PNG", marginX, y, 30, 30);
    } catch (e) {
      // Logo failed to load, continue without it
    }
  }
  doc.setFontSize(9).setFont("helvetica", "normal");
  [
    "BARCIO CERAMICHE SRL",
    "VIALE SCALA GRECA, 406",
    "96100 SIRACUSA",
    "P.IVA: 02142710892",
    "C.UNIVOCO: SU9YNJA",
    "barcioceramichesrl@gmail.com",
    "barcioceramichesrl@pec.it",
  ].forEach((text, i) => {
    const x = pageWidth - marginX - doc.getTextWidth(text);
    doc.text(text, x, y + 5 + i * 4);
  });
  y += 48;

  // ---- TITOLO DDT ----
  doc.setFontSize(16).setFont("helvetica", "bold");
  doc.text("DOCUMENTO DI TRASPORTO", pageWidth / 2, y, { align: "center" });
  y += 7;
  doc.setFontSize(11).setFont("helvetica", "normal");
  const ddtDate = ddt.date
    ? new Date(ddt.date).toLocaleDateString("it-IT", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : new Date().toLocaleDateString("it-IT", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
  const causaleText =
    ddt.causale === "consegna"
      ? `Consegna ${ddt.sequence ?? 1}`
      : CAUSALE_LABEL[ddt.causale] || ddt.causale;
  doc.text(
    `DDT N° ${ddt.id}  –  Data: ${ddtDate}  –  Causale: ${causaleText}`,
    pageWidth / 2,
    y,
    { align: "center" }
  );
  y += 8;

  // ---- LINEA SEPARATRICE ----
  doc.setDrawColor(150).line(marginX, y, pageWidth - marginX, y);
  y += 6;

  // ---- DESTINATARIO + N° PREVENTIVO ----
  doc.setFontSize(10);
  if (ddt.client?.clientName) {
    doc.setFont("helvetica", "bolditalic");
    doc.text("Committente:", marginX, y);
    const destOff = doc.getTextWidth("Committente: ") + 0.8;
    doc.setFont("helvetica", "normal");
    doc.text(ddt.client.clientName, marginX + destOff, y);
  }
  if (ddt.quoteId) {
    doc.setFont("helvetica", "bolditalic");
    const prevLabel = `Preventivo N° ${ddt.quoteId}`;
    doc.text(prevLabel, pageWidth - marginX - doc.getTextWidth(prevLabel), y);
  }
  if (ddt.client?.clientName || ddt.quoteId) y += 6;

  // ---- TABELLA ARTICOLI ----
  const BOX_UOMS = ["SC", "CONF", "M2", "MQ"];
  const items = ddt.ddt_items || [];
  const body = items.map((it) => {
    const isBox = BOX_UOMS.includes((it.uom || "").toUpperCase());
    const qty = String(Math.round(Number(it.quantity)));
    return [
      it.description || "—",
      isBox ? qty : "—",
      isBox ? "—" : qty,
      it.uom || "—",
    ];
  });

  autoTable(doc, {
    startY: y,
    theme: "grid",
    head: [["Descrizione", "N. Scatole", "N. Pezzi", "UdM"]],
    body,
    margin: { left: marginX, right: marginX },
    styles: { fontSize: 10, cellPadding: 1.5 },
    headStyles: { fillColor: [200, 200, 200], fontStyle: "bold" },
    columnStyles: {
      0: { cellWidth: "auto" },
      1: { cellWidth: 28, halign: "right" },
      2: { cellWidth: 28, halign: "right" },
      3: { cellWidth: 22, halign: "center" },
    },
  });

  y = doc.lastAutoTable.finalY + 10;

  // ---- SPAZIO FIRME (gray box, due colonne) ----
  if (y + 50 > pageHeight - 20) {
    doc.addPage();
    y = 20;
  }

  const colPadX = 8;
  const headPadTop = 10;
  const sigLineYOffset = 12;
  const lineH = 6;
  const sigWidth = 60;
  const blockH = Math.max(40, headPadTop + 7 + lineH + sigLineYOffset + 8);

  doc.setFillColor(240, 240, 240);
  doc.rect(marginX, y, pageWidth - 2 * marginX, blockH, "F");

  const col1X = marginX + colPadX;
  const col2X = pageWidth / 2 + colPadX;

  const magNome = ddt.magazziniere?.operatorName || "—";
  const clienteNome = ddt.client?.clientName || "—";

  const renderSigCol = (x, title, name) => {
    let fy = y + headPadTop;
    doc.setFont("helvetica", "bold").setFontSize(12);
    doc.text(title, x, fy);
    fy += 7;
    doc.setFont("helvetica", "normal").setFontSize(11);
    doc.text(name, x, fy);
    fy += lineH;
    doc.setFont("helvetica", "bold").setFontSize(12);
    doc.text("Firma", x, fy);
    fy += sigLineYOffset;
    doc.setDrawColor(0);
    doc.line(x, fy, x + sigWidth, fy);
  };

  renderSigCol(col1X, "Magazziniere", magNome);
  renderSigCol(col2X, "Committente", clienteNome);

  y += blockH + 8;

  // ---- CONDIZIONI GENERALI ----
  const conditions = [
    "1. La merce va controllata allo scarico. Al seguito, non si accettano più reclami.",
    "2. La merce viaggia a rischio e pericolo del Cliente, anche se convenuta franco a destino.",
    "3. Salvo patto espresso, tutti i prezzi si intendono franco sede della Ditta venditrice.",
    "4. In caso di ritardo nel pagamento decorreranno gli interessi commerciali nella misura del saggio ufficiale dello sconto aumentato di tre punti.",
    "5. Per qualsiasi controversia si adirà l'Autorità Giudiziaria nel territorio della Ditta venditrice.",
    "6. Non si rilasciano quietanze separate: per il regolare saldo si prega di rimandare la fattura. Comunicateci eventuali errori / omissioni relative ai Vostri dati anagrafici e fiscali. Senza Vs. comunicazioni resteranno validi quelli qui riportati e ci riterremo liberi da ogni responsabilità e sanzione.",
  ];

  const maxTextWidth = pageWidth - 2 * marginX;

  doc.setDrawColor(150).line(marginX, y, pageWidth - marginX, y);
  y += 4;

  doc.setFont("helvetica", "bold").setFontSize(7);
  doc.text("CONDIZIONI GENERALI", marginX, y);
  y += 4;

  doc.setFont("helvetica", "normal").setFontSize(6.5);
  for (const line of conditions) {
    const wrapped = doc.splitTextToSize(line, maxTextWidth);
    if (y + wrapped.length * 3.5 > pageHeight - 8) {
      doc.addPage();
      y = 15;
    }
    doc.text(wrapped, marginX, y);
    y += wrapped.length * 3.5 + 1;
  }

  return doc.output("blob");
}
